import React from "react";
import { fetchQuery, graphql } from "react-relay";
import { components, defaultTheme, type DropdownIndicatorProps, type GroupBase } from "react-select";
import Async, { type AsyncProps } from "react-select/async";

import { faAt, faHashtag } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faCaretDown } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";

import { zindexModal } from "~/css/zindexes";
import { environment } from "~/models";
import { colors } from "~/ui";
import fellowSelectProps from "~/ui/react-select";

import type { ZoomChannelSelectQuery as QueryType } from "~/__generated__/ZoomChannelSelectQuery.graphql";

const ZoomChannelSelectQuery = graphql`
	query ZoomChannelSelectQuery($name: String) {
		viewer {
			account {
				integrations {
					zoomBot {
						channels(name: $name) {
							id
							name
							isUser
						}
					}
				}
			}
		}
	}
`;

export interface ChannelNode {
	id: string;
	name: string;
	isUser: boolean;
}

export interface Channel extends ChannelNode {
	label: string;
	value: string | null;
}

function loadOptions(inputValue: string, callback: (options: Channel[]) => void): void {
	fetchQuery<QueryType>(environment, ZoomChannelSelectQuery, { name: inputValue.replace("#", "") })
		.toPromise()
		.then(result => {
			const data = result?.viewer.account.integrations.zoomBot?.channels ?? [];
			const channels: Channel[] = data.map((channel: ChannelNode) => ({
				...channel,
				label: channel.name,
				value: channel.name,
			}));
			callback(channels);
		});
}

// Hack to work around this not being exported anywhere
const DropdownIndicator: NonNullable<typeof components.DropdownIndicator> = components.DropdownIndicator as NonNullable<
	typeof components.DropdownIndicator
>;

function CustomDropdownIndicator<T>(props: DropdownIndicatorProps<T, false>): React.ReactElement {
	return (
		<DropdownIndicator {...props}>
			<FontAwesomeIcon icon={faCaretDown} size="sm" />
		</DropdownIndicator>
	);
}

function formatChannel(opt: Channel): React.ReactNode {
	let icon = faHashtag;
	if (opt.isUser) icon = faAt;

	return (
		<>
			<FontAwesomeIcon icon={icon} size="sm" /> {opt.label}
		</>
	);
}

export const ZoomChannelSelect: React.FunctionComponent<
	Pick<AsyncProps<Channel, false, GroupBase<Channel>>, "onChange" | "placeholder" | "className" | "value">
> = props => {
	return (
		<Async
			{...fellowSelectProps}
			{...props}
			className={cx(props.className, fellowSelectProps.className)}
			cacheOptions
			defaultOptions
			isSearchable
			loadOptions={loadOptions}
			formatOptionLabel={formatChannel}
			menuPortalTarget={document.body}
			menuPosition="fixed"
			components={{
				IndicatorSeparator: null,
				DropdownIndicator: CustomDropdownIndicator,
			}}
			styles={{
				control: (provided, state) => ({
					...provided,
					boxShadow: state.isFocused ? `0 0 0 3px ${colors.royal[100]}` : "none",
				}),
				menuPortal: base => ({ ...base, zIndex: zindexModal + 1 }),
			}}
			theme={{
				...defaultTheme,
				colors: { ...defaultTheme.colors, primary: colors.royal[400], primary25: colors.royal[100] },
			}}
		/>
	);
};
